import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginWithWebLink } from '../services/authService';
import { AUTH_ROUTES } from './authRoutes';
import { DASHBOARD_ROUTES } from '../homeDashboard/dashboardRoutes';
import logo from '../assets/images/logo.png';
import './WebLinkLogin.css';

let pendingExchange = null;
let pendingToken = null;

function exchangeOnce(token) {
  if (pendingToken !== token) {
    pendingToken = token;
    pendingExchange = loginWithWebLink(token);
    pendingExchange.finally(() => {
      if (pendingToken === token) {
        pendingToken = null;
        pendingExchange = null;
      }
    }).catch(() => {});
  }
  return pendingExchange;
}

function getLinkError(error) {
  if (error?.response?.status === 400) {
    return {
      title: 'This link has expired',
      message: 'Login links work only once and are available for a limited time. Ask the admin for a new link, or sign in with your account details.',
    };
  }
  if (error?.request && !error?.response) {
    return {
      title: 'Connection interrupted',
      message: 'We could not reach the login service. Check your connection and try opening the link again.',
    };
  }
  return {
    title: 'We could not sign you in',
    message: 'Please ask the admin for a new login link, or sign in with your account details.',
  };
}

function StatusIcon({ status }) {
  if (status === 'checking') {
    return (
      <span className="web-link-login__spinner" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none"><rect x="5" y="10" width="14" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
      </span>
    );
  }

  return (
    <span className={`web-link-login__state-icon web-link-login__state-icon--${status}`} aria-hidden="true">
      {status === 'success' ? (
        <svg viewBox="0 0 24 24" fill="none"><path d="m5 12 4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none"><path d="M12 7v6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /><circle cx="12" cy="17" r="1.25" fill="currentColor" /></svg>
      )}
    </span>
  );
}

function WebLinkLogin() {
  const navigate = useNavigate();
  const { login, isInitialized } = useAuth();
  const completed = useRef(false);
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token'));
  const [status, setStatus] = useState('checking');
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    if (!token) {
      setFailure({
        title: 'This link is incomplete',
        message: 'The login token is missing. Ask the admin to send you a new link, or sign in with your account details.',
      });
      setStatus('error');
      return undefined;
    }
    if (!isInitialized || completed.current) return undefined;

    let active = true;
    exchangeOnce(token)
      .then(({ payload, meta }) => {
        if (!active || completed.current) return;
        if (!payload?.access_token || !payload?.user) {
          throw new Error(meta?.message || 'Could not sign in with this link.');
        }

        login({
          accessToken: payload.access_token,
          user: payload.user,
          expiresIn: payload.expires_in,
          tokenType: payload.token_type,
        });
        completed.current = true;
        setStatus('success');
      })
      .catch((requestError) => {
        if (active && !completed.current) {
          setFailure(getLinkError(requestError));
          setStatus('error');
        }
      });

    return () => { active = false; };
  }, [token, isInitialized, login]);

  useEffect(() => {
    if (status !== 'success') return undefined;
    const timer = window.setTimeout(() => navigate(DASHBOARD_ROUTES.home, { replace: true }), 1400);
    return () => window.clearTimeout(timer);
  }, [status, navigate]);

  const title = status === 'checking'
    ? 'Signing you in securely'
    : status === 'success' ? 'You’re signed in' : failure?.title;
  const message = status === 'checking'
    ? 'We’re checking your login link and preparing your account.'
    : status === 'success' ? 'Your account is ready. Opening your dashboard now.' : failure?.message;

  return (
    <main className="web-link-login" aria-busy={status === 'checking'}>
      <div className="web-link-login__glow web-link-login__glow--one" aria-hidden="true" />
      <div className="web-link-login__glow web-link-login__glow--two" aria-hidden="true" />
      <section className="web-link-login__card" aria-live="polite">
        <img className="web-link-login__logo" src={logo} alt="Vidyakasam Education" />
        <div className="web-link-login__eyebrow"><span className="web-link-login__eyebrow-dot" /> Secure account access</div>
        <StatusIcon status={status} />
        <h1>{title}</h1>
        <p className="web-link-login__message" role={status === 'error' ? 'alert' : undefined}>{message}</p>

        {status === 'checking' && (
          <div className="web-link-login__steps" aria-label="Login progress">
            <div className="web-link-login__step web-link-login__step--active"><span className="web-link-login__step-dot" /> Verifying login link</div>
            <div className="web-link-login__step"><span className="web-link-login__step-dot" /> Opening your dashboard</div>
          </div>
        )}

        {status === 'success' && (
          <Link className="web-link-login__button" to={DASHBOARD_ROUTES.home}>Open dashboard now <span aria-hidden="true">→</span></Link>
        )}

        {status === 'error' && (
          <>
            <Link className="web-link-login__button" to={AUTH_ROUTES.signIn}>Go to sign in <span aria-hidden="true">→</span></Link>
            <p className="web-link-login__help">Need a new link? Contact the admin who sent this one.</p>
          </>
        )}
      </section>
      <p className="web-link-login__footer">Vidyakasam Education · Secure sign in</p>
    </main>
  );
}

export default WebLinkLogin;
